import {
  apiErrorSchema,
  authResultSchema,
  authSuccessSchema,
  messageResponseSchema,
  type AuthResult,
  type AuthSuccess,
  type ForgotPasswordInput,
  type LoginInput,
  type MessageResponse,
  type ResendVerificationInput,
  type ResetPasswordInput,
  type SignupInput,
  type VerifyEmailInput,
} from "@reelio/contracts";
import type { ZodType } from "zod";

export class ApiRequestError extends Error {
  readonly code: string;
  readonly fieldErrors?: Record<string, string[]>;

  constructor({
    code,
    message,
    fieldErrors,
  }: {
    code: string;
    message: string;
    fieldErrors?: Record<string, string[]>;
  }) {
    super(message);
    this.name = "ApiRequestError";
    this.code = code;
    this.fieldErrors = fieldErrors;
  }
}

const request = async <T>({
  path,
  schema,
  method = "GET",
  body,
  signal,
}: {
  path: string;
  schema: ZodType<T>;
  method?: "GET" | "POST";
  body?: unknown;
  signal?: AbortSignal;
}): Promise<T> => {
  const response = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: body === undefined ? undefined : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new ApiRequestError({
      code: "unexpected_response",
      message: "Reelio returned an unexpected response.",
    });
  }

  if (!response.ok) {
    const parsedError = apiErrorSchema.safeParse(payload);

    if (parsedError.success) {
      throw new ApiRequestError(parsedError.data.error);
    }

    throw new ApiRequestError({
      code: "unexpected_response",
      message: "Reelio could not complete the request.",
    });
  }

  return schema.parse(payload);
};

export const signup = (input: SignupInput): Promise<AuthResult> =>
  request({
    path: "/api/auth/signup",
    method: "POST",
    body: input,
    schema: authResultSchema,
  });

export const login = (input: LoginInput): Promise<AuthSuccess> =>
  request({
    path: "/api/auth/login",
    method: "POST",
    body: input,
    schema: authSuccessSchema,
  });

export const getCurrentUser = (signal?: AbortSignal): Promise<AuthSuccess> =>
  request({
    path: "/api/auth/me",
    schema: authSuccessSchema,
    signal,
  });

export const logout = (): Promise<{ ok: boolean }> =>
  request({
    path: "/api/auth/logout",
    method: "POST",
    body: {},
    schema: messageResponseSchema.pick({ ok: true }),
  });

export const requestPasswordReset = (
  input: ForgotPasswordInput,
): Promise<MessageResponse> =>
  request({
    path: "/api/auth/forgot-password",
    method: "POST",
    body: input,
    schema: messageResponseSchema,
  });

export const resetPassword = (
  input: ResetPasswordInput,
): Promise<MessageResponse> =>
  request({
    path: "/api/auth/reset-password",
    method: "POST",
    body: input,
    schema: messageResponseSchema,
  });

export const verifyEmail = (
  input: VerifyEmailInput,
): Promise<AuthSuccess> =>
  request({
    path: "/api/auth/verify-email",
    method: "POST",
    body: input,
    schema: authSuccessSchema,
  });

export const resendVerification = (
  input: ResendVerificationInput,
): Promise<MessageResponse> =>
  request({
    path: "/api/auth/resend-verification",
    method: "POST",
    body: input,
    schema: messageResponseSchema,
  });
